/**
 * useThemeEditor - Hook for theme editing with database persistence
 *
 * Manages theme state, live preview via postMessage, and database sync.
 * Replaces shadcn-studio's localStorage-based useSettings.
 */

import { useState, useCallback, useEffect, useRef } from "react";

import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { themesApi } from "@/lib/api/themes";
import { parseThemeContentSafe } from "@/components/editor/themes/theme-card-state";
import { previewMessage } from "@/lib/visual-editor/post-message";
import { useThemeHistoryStore } from "@/stores/themeHistoryStore";
import type {
  Theme,
  ThemeContent,
  ThemeMode,
  ThemeStyleProps,
  UseThemeEditorProps,
  UseThemeEditorReturn,
} from "@/types/theme.types";

export function useThemeEditor({
  workspaceId,
  previewIframe,
  projectId,
}: UseThemeEditorProps): UseThemeEditorReturn {
  const { getIdToken } = useAuth();
  const { toast } = useToast();

  // State
  const [themes, setThemes] = useState<Theme[]>([]);
  const [currentTheme, setCurrentTheme] = useState<Theme | null>(null);
  const [selectedThemeId, setSelectedThemeId] = useState<string | null>(null);
  const [mode, setMode] = useState<ThemeMode>("light");
  const [styles, setStyles] = useState<{
    light: ThemeStyleProps;
    dark: ThemeStyleProps;
  }>({ light: {}, dark: {} });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isImportedTheme, setIsImportedTheme] = useState(false);

  // Undo/Redo history store - subscribe to state for proper reactivity
  const historyStore = useThemeHistoryStore();
  // Subscribe to history state to trigger re-renders when undo/redo state changes
  const historyLength = useThemeHistoryStore((state) => state.history.length);
  const historyIndex = useThemeHistoryStore((state) => state.currentIndex);
  const isUndoRedoAction = useRef(false);
  const isThemeSwitchAction = useRef(false);
  const historyInitializedRef = useRef(false);

  // Track original styles for change detection (updates when theme is selected)
  const originalStylesRef = useRef<{
    light: ThemeStyleProps;
    dark: ThemeStyleProps;
  }>({ light: {}, dark: {} });

  // Track project's applied theme for Reset button (set once, doesn't change when browsing themes)
  const projectAppliedThemeRef = useRef<{
    themeId: string | null;
    styles: { light: ThemeStyleProps; dark: ThemeStyleProps };
  }>({ themeId: null, styles: { light: {}, dark: {} } });

  // Track if project theme has been initialized
  const projectThemeInitializedRef = useRef(false);

  // Track current styles for preview (avoids circular dependency)
  const stylesRef = useRef(styles);
  stylesRef.current = styles;

  // Track previous styles to avoid sending duplicate previews
  const prevStylesRef = useRef<string>("");

  // Derived state
  const selectedTheme = themes.find((t) => t.themeId === selectedThemeId) || null;
  const isEditingBuiltIn = selectedTheme?.type === "built-in";
  const hasChanges =
    JSON.stringify(styles) !== JSON.stringify(originalStylesRef.current);
  // Use subscribed state values for proper reactivity with keyboard shortcuts
  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < historyLength - 1;
  // User is editing from scratch (no theme selected, not imported, but has made edits)
  const hasStyles = Object.keys(styles.light).length > 0 || Object.keys(styles.dark).length > 0;
  const isScratchTheme = !selectedThemeId && !isImportedTheme && hasStyles;

  // Load themes from database (silent = true skips loading spinner)
  const loadThemes = useCallback(async (silent?: boolean) => {
    const token = await getIdToken();
    if (!token) {
      if (!silent) setIsLoading(false);
      setError("Please sign in to view themes");
      return;
    }

    setError(null);
    try {
      if (!silent) setIsLoading(true);
      const data = await themesApi.listThemes(token, workspaceId);
      setThemes(data);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load themes";
      console.error("[useThemeEditor] Failed to fetch themes:", err);
      setError(errorMessage);
      toast({
        title: "Error",
        description: "Failed to load themes. Please try again.",
        variant: "destructive",
      });
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [getIdToken, workspaceId, toast]);

  // Parse theme content JSON (double-encode tolerant — see #54).
  const parseThemeContent = (content: string): ThemeContent | null => {
    const parsed = parseThemeContentSafe(content);
    if (!parsed) {
      console.error("[useThemeEditor] Failed to parse theme content");
    }
    return parsed;
  };

  // Select a theme and load its styles
  const selectTheme = useCallback(
    (themeId: string) => {
      const theme = themes.find((t) => t.themeId === themeId);
      if (!theme) return;
      setCurrentTheme(theme);
      setSelectedThemeId(themeId);
      setIsImportedTheme(false); // Clear imported state when selecting a theme
      const content = parseThemeContent(theme.content);
      if (content) {
        const newStyles = {
          light: content.cssVars.light || {},
          dark: content.cssVars.dark || {},
        };

        // Mark as theme switch to skip the debounced history push
        isThemeSwitchAction.current = true;
        setStyles(newStyles);
        originalStylesRef.current = newStyles;

        // First theme selection: init history with selected theme as baseline
        // Subsequent selections: push to history so user can undo to previous theme
        if (!historyInitializedRef.current) {
          // Initialize history with the selected theme as baseline
          // (Empty baseline for scratch editing is handled in the style change useEffect)
          historyStore.initHistory({ themeId, content: theme.content });
          historyInitializedRef.current = true;
        } else {
          // Push theme change to history so user can undo to previous theme
          historyStore.pushHistory({ themeId, content: theme.content });
        }

        // Track the project's applied theme (first selection only)
        // This is used by Reset to go back to project's original theme
        if (!projectThemeInitializedRef.current) {
          projectAppliedThemeRef.current = { themeId, styles: newStyles };
          projectThemeInitializedRef.current = true;
        }

        // Send preview immediately (fonts are cached via INIT_THEME on iframe load)
        if (previewIframe) {
          previewMessage.updateTheme(previewIframe, themeId, theme.content);
        }
      }
    },
    [themes, previewIframe, historyStore]
  );

  // Fetch a theme by ID from API and select it
  const fetchAndSelectTheme = useCallback(
    async (themeId: string) => {
      console.log('fetchAndSelectTheme',themeId);
      const token = await getIdToken();
      if (!token) {
        toast({
          title: "Error",
          description: "Please sign in to load theme",
          variant: "destructive",
        });
        return;
      }

      try {
        setIsLoading(true);
        const theme = await themesApi.getTheme(token, themeId);

        // Update themes list if not already present
        setThemes((prev) => {
          const exists = prev.some((t) => t.themeId === theme.themeId);
          return exists ? prev : [...prev, theme];
        });

        // Select the theme and load its styles
        setSelectedThemeId(theme.themeId);
        setCurrentTheme(theme);
        setIsImportedTheme(false); // Clear imported state
        const content = parseThemeContent(theme.content);
        if (content) {
          const newStyles = {
            light: content.cssVars.light || {},
            dark: content.cssVars.dark || {},
          };
          setStyles(newStyles);
          originalStylesRef.current = newStyles;

          // Send preview immediately (fonts are cached via INIT_THEME on iframe load)
          if (previewIframe) {
            previewMessage.updateTheme(previewIframe, theme.themeId, theme.content);
          }
        }
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load theme";
        console.error("[useThemeEditor] Failed to fetch theme:", err);
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [getIdToken, previewIframe, toast]
  );

  // Update a single style property (current mode only)
  const updateStyle = useCallback(
    (key: string, value: string) => {
      setStyles((prev) => ({
        ...prev,
        [mode]: {
          ...prev[mode],
          [key]: value,
        },
      }));
    },
    [mode]
  );

  // Update style in both modes (for fonts, radius, shadows)
  const updateBothModes = useCallback((updates: Partial<ThemeStyleProps>) => {
    setStyles((prev) => {
      // Filter out undefined values to maintain type safety
      const filteredUpdates = Object.fromEntries(
        Object.entries(updates).filter(([, value]) => value !== undefined)
      ) as ThemeStyleProps;

      return {
        light: { ...prev.light, ...filteredUpdates },
        dark: { ...prev.dark, ...filteredUpdates },
      };
    });
  }, []);

  // Set light and dark styles independently (for CSS import)
  // This avoids the async state issue with updateStyle
  const setLightAndDarkStyles = useCallback(
    (
      lightUpdates: Partial<ThemeStyleProps>,
      darkUpdates: Partial<ThemeStyleProps>
    ) => {
      setStyles((prev) => {
        const filteredLight = Object.fromEntries(
          Object.entries(lightUpdates).filter(([, value]) => value !== undefined)
        ) as ThemeStyleProps;

        const filteredDark = Object.fromEntries(
          Object.entries(darkUpdates).filter(([, value]) => value !== undefined)
        ) as ThemeStyleProps;

        return {
          light: { ...prev.light, ...filteredLight },
          dark: { ...prev.dark, ...filteredDark },
        };
      });
    },
    []
  );

  // Change mode and send to iframe
  const handleModeChange = useCallback(
    (newMode: ThemeMode) => {
      setMode(newMode);
      // Send mode change to iframe
      if (previewIframe) {
        previewMessage.setThemeMode(previewIframe, newMode);
      }
    },
    [previewIframe]
  );

  // Send current styles to iframe for preview (uses ref to avoid circular dependency)
  const sendPreviewToIframe = useCallback(() => {
    console.log('sendPreviewToIframe',previewIframe);
    if (!previewIframe) return;

    // Use placeholder ID: "imported" for imported themes, "scratch" for editing without theme
    const themeIdToSend = selectedThemeId || (isImportedTheme ? "imported" : "scratch");

    // Build theme content from current styles (using ref)
    const themeContent: ThemeContent = {
      name: selectedTheme?.name || (isImportedTheme ? "Imported Theme" : "Custom Theme"),
      type: "registry:theme",
      cssVars: stylesRef.current,
    };

    previewMessage.updateTheme(
      previewIframe,
      themeIdToSend,
      JSON.stringify(themeContent)
    );
  }, [previewIframe, selectedThemeId, selectedTheme?.name, isImportedTheme]);

  // Auto-send preview when styles change (with deduplication)
  useEffect(() => {
    const stylesJson = JSON.stringify(styles);

    // Skip if styles haven't actually changed
    if (stylesJson === prevStylesRef.current) {
      return;
    }

    const debounceTimer = setTimeout(() => {
      // Double-check styles haven't changed during debounce
      const currentStylesJson = JSON.stringify(stylesRef.current);
      if (currentStylesJson !== prevStylesRef.current) {
        prevStylesRef.current = currentStylesJson;
        sendPreviewToIframe();
      }
    }, 100);

    return () => clearTimeout(debounceTimer);
  }, [styles, sendPreviewToIframe]);

  // Reset to project's applied theme (not just current selection)
  const resetToOriginal = useCallback(() => {
    const projectTheme = projectAppliedThemeRef.current;
    if (projectTheme.themeId) {
      // Reset to project's applied theme
      setSelectedThemeId(projectTheme.themeId);
      setCurrentTheme(null);
      setStyles(projectTheme.styles);
      originalStylesRef.current = projectTheme.styles;
      setIsImportedTheme(false);

      // Build content JSON for history
      const content: ThemeContent = {
        name: themes.find((t) => t.themeId === projectTheme.themeId)?.name || "Theme",
        type: "registry:theme",
        cssVars: projectTheme.styles,
      };
      historyStore.initHistory({ themeId: projectTheme.themeId, content: JSON.stringify(content) });

      // Send to iframe
      if (previewIframe) {
        previewMessage.updateTheme(previewIframe, projectTheme.themeId, JSON.stringify(content));
      }
    } else {
      // No project theme - just reset styles
      setStyles(originalStylesRef.current);
    }
  }, [historyStore, themes, previewIframe]);

  // Track style changes and push to history (debounced)
  useEffect(() => {
    // Skip during undo/redo or theme switch actions
    if (isUndoRedoAction.current || isThemeSwitchAction.current) {
      isUndoRedoAction.current = false;
      isThemeSwitchAction.current = false;
      return;
    }

    // Check if styles have any values (user has made edits)
    const hasStyles = Object.keys(styles.light).length > 0 || Object.keys(styles.dark).length > 0;
    if (!hasStyles) return;

    const timer = setTimeout(() => {
      // Build content JSON from current styles
      const content: ThemeContent = {
        name: selectedTheme?.name || (isImportedTheme ? "Imported Theme" : "Custom Theme"),
        type: "registry:theme",
        cssVars: styles,
      };

      // If history is empty (editing from scratch), initialize it first
      if (historyStore.history.length === 0 && !historyInitializedRef.current) {
        // Initialize with empty state, then push current
        const emptyContent: ThemeContent = {
          name: "No Theme",
          type: "registry:theme",
          cssVars: { light: {}, dark: {} },
        };
        historyStore.initHistory({ themeId: null, content: JSON.stringify(emptyContent) });
        historyInitializedRef.current = true;
      }

      historyStore.pushHistory({
        themeId: selectedThemeId,
        content: JSON.stringify(content),
      });
    }, 300); // Debounce to avoid too many history entries

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styles, isImportedTheme]);

  // Undo
  const undo = useCallback(() => {
    const snapshot = historyStore.undo();
    if (snapshot) {
      isUndoRedoAction.current = true;
      // Parse content to get styles
      const content = parseThemeContent(snapshot.content);
      if (content) {
        const newStyles = {
          light: content.cssVars.light || {},
          dark: content.cssVars.dark || {},
        };
        setStyles(newStyles);
        // Update selectedThemeId if it changed (user undid a theme switch)
        if (snapshot.themeId !== selectedThemeId) {
          setSelectedThemeId(snapshot.themeId);
          setCurrentTheme(null);
        }
        // Send to iframe
        if (previewIframe) {
          previewMessage.updateTheme(previewIframe, snapshot.themeId || "imported", snapshot.content);
        }
      }
    }
  }, [historyStore, previewIframe, selectedThemeId]);

  // Redo
  const redo = useCallback(() => {
    const snapshot = historyStore.redo();
    if (snapshot) {
      isUndoRedoAction.current = true;
      // Parse content to get styles
      const content = parseThemeContent(snapshot.content);
      if (content) {
        const newStyles = {
          light: content.cssVars.light || {},
          dark: content.cssVars.dark || {},
        };
        setStyles(newStyles);
        // Update selectedThemeId if it changed (user redid a theme switch)
        if (snapshot.themeId !== selectedThemeId) {
          setSelectedThemeId(snapshot.themeId);
          setCurrentTheme(null);
        }
        // Send to iframe
        if (previewIframe) {
          previewMessage.updateTheme(previewIframe, snapshot.themeId || "imported", snapshot.content);
        }
      }
    }
  }, [historyStore, previewIframe, selectedThemeId]);

  // Clear selected theme (for CSS import - keeps styles as temporary)
  const clearSelectedTheme = useCallback(() => {
    setSelectedThemeId(null);
    setCurrentTheme(null);
    setIsImportedTheme(true);
    // Set original to empty so hasChanges will be true (unsaved state)
    originalStylesRef.current = { light: {}, dark: {} };
  }, []);

  // Import theme (completely replaces styles - used for Agent Chat theme import)
  const importTheme = useCallback(
    (lightStyles: ThemeStyleProps, darkStyles: ThemeStyleProps) => {
      // Clear any selected theme
      setSelectedThemeId(null);
      setCurrentTheme(null);
      setIsImportedTheme(true);

      // Replace styles completely (not merge)
      const newStyles = {
        light: lightStyles || {},
        dark: darkStyles || {},
      };
      setStyles(newStyles);

      // Set original to empty so hasChanges will be true (unsaved state)
      originalStylesRef.current = { light: {}, dark: {} };
    },
    []
  );

  // Reset to default (first) theme
  const resetToDefault = useCallback(() => {
    if (themes.length > 0) {
      const defaultTheme =
        themes.find((t) => t.themeId === "default") || themes[0];
      selectTheme(defaultTheme.themeId);
    }
  }, [themes, selectTheme]);

  // Save as new custom theme
  const saveAsCustomTheme = useCallback(
    async (name: string, customPreviewColors?: string[], silent?: boolean): Promise<Theme | null> => {
      const token = await getIdToken();
      if (!token) {
        toast({
          title: "Error",
          description: "Please sign in to save themes",
          variant: "destructive",
        });
        return null;
      }

      setIsSaving(true);
      try {
        // Build theme content
        const content: ThemeContent = {
          name,
          type: "registry:theme",
          cssVars: styles,
        };

        // Use custom preview colors if provided, otherwise generate from styles
        const previewColors = customPreviewColors || [
          styles.light.primary || "#888888",
          styles.light.secondary || "#888888",
          styles.light.accent || "#888888",
          styles.light["chart-1"] || "#888888",
        ];

        const result = await themesApi.createTheme(token, workspaceId, {
          name,
          content: JSON.stringify(content),
          colors: previewColors,
        });

        // Reload themes silently (no loading spinner)
        await loadThemes(true);

        if (!silent) {
          toast({
            title: "Theme saved",
            description: `"${name}" has been saved successfully`,
          });
        }

        // Select the new theme and clear imported state
        if (result.themeId) {
          setSelectedThemeId(result.themeId);
          setCurrentTheme(result);
          setIsImportedTheme(false); // Clear imported state - now it's a saved theme
          originalStylesRef.current = styles; // Update original styles ref

          // Initialize or push to history with the new theme
          const themeContent = JSON.stringify(content);
          if (!historyInitializedRef.current) {
            historyStore.initHistory({ themeId: result.themeId, content: themeContent });
            historyInitializedRef.current = true;
          } else {
            historyStore.pushHistory({ themeId: result.themeId, content: themeContent });
          }
        }

        return result as Theme;
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to save theme";
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [getIdToken, workspaceId, styles, loadThemes, toast]
  );

  // Update existing custom theme
  const updateCustomTheme = useCallback(async (silent?: boolean): Promise<boolean> => {
    const token = await getIdToken();
    if (!token) {
      toast({
        title: "Error",
        description: "Please sign in to update themes",
        variant: "destructive",
      });
      return false;
    }

    // Find the current theme
    const theme = themes.find((t) => t.themeId === selectedThemeId);
    if (!theme) {
      toast({
        title: "Error",
        description: "Theme not found",
        variant: "destructive",
      });
      return false;
    }

    // Can only update custom themes
    if (theme.type !== "custom") {
      toast({
        title: "Error",
        description: "Cannot update built-in themes",
        variant: "destructive",
      });
      return false;
    }

    setIsSaving(true);
    try {
      // Build updated theme content
      const content: ThemeContent = {
        name: theme.name,
        type: "registry:theme",
        cssVars: styles,
      };

      // Generate preview colors from styles
      const previewColors = [
        styles.light.primary || "#888888",
        styles.light.secondary || "#888888",
        styles.light.accent || "#888888",
        styles.light["chart-1"] || "#888888",
      ];

      await themesApi.updateTheme(token, theme.id, {
        content: JSON.stringify(content),
        colors: previewColors,
      });

      // Update local state
      setThemes((prev) =>
        prev.map((t) =>
          t.id === theme.id
            ? { ...t, content: JSON.stringify(content), colors: previewColors }
            : t
        )
      );

      // Update original styles ref to reflect saved state
      originalStylesRef.current = { ...styles };

      if (!silent) {
        toast({
          title: "Theme updated",
          description: `"${theme.name}" has been updated`,
        });
      }

      return true;
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to update theme";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [getIdToken, themes, selectedThemeId, styles, toast]);

  // Delete a custom theme
  const deleteTheme = useCallback(
    async (themeId: string): Promise<boolean> => {
      const token = await getIdToken();
      if (!token) {
        toast({
          title: "Error",
          description: "Please sign in to delete themes",
          variant: "destructive",
        });
        return false;
      }

      // Find the theme to get its ID (UUID)
      const theme = themes.find((t) => t.themeId === themeId);
      if (!theme) {
        toast({
          title: "Error",
          description: "Theme not found",
          variant: "destructive",
        });
        return false;
      }

      // Can only delete custom themes
      if (theme.type !== "custom") {
        toast({
          title: "Error",
          description: "Cannot delete built-in themes",
          variant: "destructive",
        });
        return false;
      }

      try {
        await themesApi.deleteTheme(token, theme.id);

        // Remove from local state
        setThemes((prev) => prev.filter((t) => t.id !== theme.id));

        // If deleted theme was selected, clear selection
        if (selectedThemeId === themeId) {
          setSelectedThemeId(null);
          setCurrentTheme(null);
          setStyles({ light: {}, dark: {} });
          originalStylesRef.current = { light: {}, dark: {} };
        }

        toast({
          title: "Theme deleted",
          description: `"${theme.name}" has been deleted`,
        });

        return true;
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to delete theme";
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
        return false;
      }
    },
    [getIdToken, themes, selectedThemeId, toast]
  );

  // Load themes on mount
  useEffect(() => {
    loadThemes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  // Fetch project's applied theme on mount
  useEffect(() => {
    if (!projectId) return;

    const fetchProjectTheme = async () => {
      const token = await getIdToken();
      if (!token) return;

      try {
        const projectTheme = await themesApi.getProjectTheme(token, projectId);
        if (projectTheme) {
          // Applied-theme snapshot is a subset of Theme (no id/colors) — the
          // consumers below only read themeId/name/content.
          setCurrentTheme(projectTheme as unknown as Theme);
          // Also set projectAppliedThemeRef for Reset functionality
          const rawContent = projectTheme.content;
          const content = rawContent ? parseThemeContent(rawContent) : null;
          if (content && !projectThemeInitializedRef.current) {
            projectAppliedThemeRef.current = {
              themeId: projectTheme.themeId,
              styles: {
                light: content.cssVars.light || {},
                dark: content.cssVars.dark || {},
              },
            };
            projectThemeInitializedRef.current = true;
          }
          // Initialize history with project's applied theme so keyboard shortcuts work
          if (!historyInitializedRef.current && content && rawContent) {
            historyStore.initHistory({ themeId: projectTheme.themeId, content: rawContent });
            historyInitializedRef.current = true;
          }
        } else {
          // Project has no applied theme - initialize history with empty state
          // so user can undo back to "Choose Theme" after selecting a theme
          if (!historyInitializedRef.current) {
            const emptyContent: ThemeContent = {
              name: "No Theme",
              type: "registry:theme",
              cssVars: { light: {}, dark: {} },
            };
            historyStore.initHistory({ themeId: null, content: JSON.stringify(emptyContent) });
            historyInitializedRef.current = true;
          }
        }
      } catch {
        // Silent fail - project may not have an applied theme
        // Initialize history with empty state for "Choose Theme"
        console.debug("[useThemeEditor] No applied theme for project");
        if (!historyInitializedRef.current) {
          const emptyContent: ThemeContent = {
            name: "No Theme",
            type: "registry:theme",
            cssVars: { light: {}, dark: {} },
          };
          historyStore.initHistory({ themeId: null, content: JSON.stringify(emptyContent) });
          historyInitializedRef.current = true;
        }
      }
    };

    fetchProjectTheme();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Refs for keyboard shortcut handler to avoid stale closures
  const undoRef = useRef(undo);
  const redoRef = useRef(redo);
  undoRef.current = undo;
  redoRef.current = redo;

  // Keyboard shortcuts for undo/redo (Cmd+Z / Cmd+Shift+Z / Cmd+Y)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Cmd (Mac) or Ctrl (Windows/Linux)
      const isMod = e.metaKey || e.ctrlKey;
      if (!isMod) return;
      if (e.key !== "z" && e.key !== "y") return;

      // Skip if focus is in an input field (let browser handle native undo)
      const target = e.target as HTMLElement;
      const isInputFocused = target.tagName === "INPUT" ||
                             target.tagName === "TEXTAREA" ||
                             target.isContentEditable;
      if (isInputFocused) return;

      // Skip if event is from an iframe
      if (target.tagName === "IFRAME") return;

      // Always prevent default to stop browser behavior (e.g., Cmd+Y opens Chrome history)
      e.preventDefault();
      e.stopPropagation();

      // Get current canUndo/canRedo directly (historyIndex/historyLength are reactive)
      const currentCanUndo = historyIndex > 0;
      const currentCanRedo = historyIndex < historyLength - 1;

      if (e.key === "z") {
        if (e.shiftKey) {
          // Cmd+Shift+Z = Redo
          if (currentCanRedo) {
            redoRef.current();
          }
        } else {
          // Cmd+Z = Undo
          if (currentCanUndo) {
            undoRef.current();
          }
        }
      } else if (e.key === "y") {
        // Cmd+Y = Redo (alternative)
        if (currentCanRedo) {
          redoRef.current();
        }
      }
    };

    // Use capture phase to catch events before other handlers
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [historyIndex, historyLength]); // Only re-run when history state changes

  return {
    // State
    themes,
    selectedTheme,
    selectedThemeId,
    mode,
    styles,
    isLoading,
    isSaving,
    error,
    isEditingBuiltIn,
    hasChanges,
    isImportedTheme,
    isScratchTheme,
    canUndo,
    canRedo,
    currentTheme,

    // Actions
    loadThemes,
    selectTheme,
    fetchAndSelectTheme,
    setMode: handleModeChange,
    updateStyle,
    updateBothModes,
    setLightAndDarkStyles,
    sendPreviewToIframe,
    resetToOriginal,
    clearSelectedTheme,
    importTheme,
    resetToDefault,
    saveAsCustomTheme,
    updateCustomTheme,
    deleteTheme,
    undo,
    redo,
    clearHistory: useCallback(() => {
      // Clear history and re-initialize with the JUST-SAVED state as base.
      // This way, after save, selecting a new theme can still be undone back
      // to the saved state.
      //
      // CRITICAL: read current styles from stylesRef, NOT from closure.
      // This callback is invoked inside async chains right after setThemes()
      // queues a re-render — `themes` from the closure has stale content.
      // Using stylesRef.current guarantees we baseline against what the user
      // actually sees on screen (which IS the saved state at this point).
      const currentTheme = themes.find((t) => t.themeId === selectedThemeId);
      if (currentTheme && selectedThemeId) {
        const currentStyles = stylesRef.current;
        const content: ThemeContent = {
          name: currentTheme.name,
          type: "registry:theme",
          cssVars: currentStyles,
        };
        historyStore.initHistory({
          themeId: selectedThemeId,
          content: JSON.stringify(content),
        });

        projectAppliedThemeRef.current = {
          themeId: selectedThemeId,
          styles: {
            light: { ...currentStyles.light },
            dark: { ...currentStyles.dark },
          },
        };
        originalStylesRef.current = {
          light: { ...currentStyles.light },
          dark: { ...currentStyles.dark },
        };
      } else {
        historyStore.clearHistory();
        historyInitializedRef.current = false;
      }
    }, [historyStore, themes, selectedThemeId]),
  };
}
